import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { DropdownModule } from 'primeng/dropdown'
  ; import { MessageService } from 'primeng/api';
import { FileSelectEvent, FileUpload, FileUploadModule } from 'primeng/fileupload';
import { ToastModule } from 'primeng/toast';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { Form, FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { CalendarModule } from 'primeng/calendar';
import { countryResponse } from '../../models/country/countryResponse';
import { UtilityService } from '../../services/utility/utility.service';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { OlaPlacesResponse } from '../../models/country/olaPlacesResponse';
import { PeerReviewService } from '../../services/peer-review/peer-review.service';
import { RegisterPeerReviewerRequest } from '../../models/super-admin/registerPeerReviewerRequest';
import { PeerReviewerState } from '../../models/enums/peerReviewerState';
import { PeerReviewerResponse } from '../../models/super-admin/peerReviewerResponse';
import { ActivatedRoute, Router } from '@angular/router';
import { UpdatePeerReviewerRequest } from '../../models/super-admin/updatePeerReviewerRequest';

interface UploadEvent {
  originalEvent: Event;
  files: File[];
}

interface place {
  name: string
}

@Component({
  selector: 'app-peer-management',
  standalone: true,
  imports: [CalendarModule, ReactiveFormsModule, FileUploadModule, DialogModule, CommonModule, FormsModule, InputTextModule, TableModule, ButtonModule, DropdownModule, ToastModule, InputTextareaModule, AutoCompleteModule],
  templateUrl: './peer-management.component.html',
  styleUrl: './peer-management.component.scss'
})
export class PeerManagementComponent implements OnInit {

  @ViewChild('fileUploader') fileUploader!: FileUpload;
  attachment: File | undefined;
  isLoading: boolean = false;

  genders: { name: string; value: number }[] = [];
  submitted: boolean = false;
  newPeerForm!: FormGroup;
  countries: countryResponse[] = [];
  places: place[] = [];
  selectedCountry: string | null = null;
  currentActivityIndex: number = 0;
  peerToEditId: string = '';
  peerToEdit: PeerReviewerResponse | undefined;
  originalEmailAddress: string | undefined;
  uploadedImageUrl: string | null | undefined;

  constructor(private messageService: MessageService,
    private formBuilder: FormBuilder,
    private utilityService: UtilityService,
    private peerReviewService: PeerReviewService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) { }

  private loadCountries(): void {
    this.utilityService.getCountries().subscribe({
      next: (response) => {
        if (response?.result) {
          this.countries = response.result;
        }
      },
      error: (err) => console.warn('Error loading countries:', err),
    });
  }

  filterPlaces(event: AutoCompleteCompleteEvent) {
    if (event.query.length > 3) {
      this.utilityService.getOLAPlaces(event.query).subscribe({
        next: (response: OlaPlacesResponse) => this.places = response.predictions.map(item => ({ name: item.description })),
      });
    } else {
      this.places = [{ name: event.query }];
    }
  }

  populatePeerForm() {
    if (this.peerToEdit) {
      this.originalEmailAddress = this.peerToEdit.email;
      this.uploadedImageUrl = this.peerToEdit.imageUrl;
      this.newPeerForm.patchValue({
        peerInformation: {
          firstName: this.peerToEdit.firstName,
          lastName: this.peerToEdit.lastName,
          emailAddress: this.peerToEdit.email,
          contactNumber: this.peerToEdit.phoneNumber,
          gender: this.genders.find(g => g.value === this.peerToEdit?.gender) || null
        },
        peerProfile: {
          city: this.peerToEdit.city,
          country: this.peerToEdit.country,
          originCity: this.peerToEdit.homeCity,
          originCountry: this.peerToEdit.homeCountry
        },
        highSchoolInformation: {
          nameOfHighSchool: this.peerToEdit.highSchoolName,
          board: this.peerToEdit.highSchoolBoard,
          coursework: this.peerToEdit.courseWork,
          yearGraduatedHighSchool: this.peerToEdit.highSchoolCompletionYear,
          classXGrades: this.peerToEdit.tenthGrades,
          classXIIGrades: this.peerToEdit.twelvethGrades
        },
        undergraduateCollegeInformation: {
          nameOfUndergraduateCollege: this.peerToEdit.underGraduateCollageName,
          countryOfUndergraduateCollege: this.peerToEdit.underGraduateCollageCountry,
          cityOfUndergraduateCollege: this.peerToEdit.underGraduateCollageCity,
          majorUndergraduate: this.peerToEdit.underGraduateMajorOrDegree,
          yearGraduatedUndergraduate: this.peerToEdit.underGraduateOrExpectedUnderGraduationCompletionYear,
          expectedGradeUndergraduate: this.peerToEdit.underGraduateExpectedGrades
        },
        graduateCollegeInformation: {
          nameOfGraduateCollege: this.peerToEdit.graduateCollageName,
          countryOfGraduateCollege: this.peerToEdit.graduateCollageCountry,
          cityOfGraduateCollege: this.peerToEdit.graduateCollageCity,
          majorGraduate: this.peerToEdit.graduateMajorOrDegree,
          yearGraduatedGraduate: this.peerToEdit.graduateOrExpectedGraduationCompletionYear,
          expectedGradeGraduate: this.peerToEdit.graduateExpectedGrades
        },
        testScores: {
          satOverall: this.peerToEdit.satOverallScore,
          satEnglish: this.peerToEdit.satEnglishScore,
          satMath: this.peerToEdit.satMathScore,
          actOverall: this.peerToEdit.actOverallScore,
          actEnglish: this.peerToEdit.actEnglishScore,
          actMath: this.peerToEdit.actMathScore,
          gre: this.peerToEdit.greScore,
          gmat: this.peerToEdit.gmatScore,
          ielts: this.peerToEdit.ieltsScore,
          toefl: this.peerToEdit.toeflScore,
          ap: this.peerToEdit.apScore
        },
        extracurricularActivities: this.peerToEdit.extraCurricularActivitiesDescription,
        shortBio: this.peerToEdit.shortBio
      });
      const employmentArray = this.newPeerForm.get('employmentHistory') as FormArray;
      employmentArray.clear();

      this.peerToEdit.employmentHistory?.forEach(history => {
        employmentArray.push(this.formBuilder.group({
          companyName: history.companyName, // Wrap string as object
          role: history.role
        }));
      });
    }
  }

  initializeForm() {
    this.newPeerForm = this.formBuilder.group({
      peerInformation: this.formBuilder.group({
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        emailAddress: ['', [Validators.required, Validators.pattern('[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$')]],
        contactNumber: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]],

        gender: ['', Validators.required]
      }),
      peerProfile: this.formBuilder.group({
        city: [''],
        country: [''],
        originCity: [''],
        originCountry: ['']
      }),
      highSchoolInformation: this.formBuilder.group({
        nameOfHighSchool: [''],
        board: [''],
        coursework: [''],
        yearGraduatedHighSchool: [''],
        classXGrades: [''],
        classXIIGrades: ['']
      }),
      undergraduateCollegeInformation: this.formBuilder.group({
        nameOfUndergraduateCollege: [''],
        countryOfUndergraduateCollege: [''],
        cityOfUndergraduateCollege: [''],
        majorUndergraduate: [''],
        yearGraduatedUndergraduate: [''],
        expectedGradeUndergraduate: ['']
      }),
      graduateCollegeInformation: this.formBuilder.group({
        nameOfGraduateCollege: [''],
        countryOfGraduateCollege: [''],
        cityOfGraduateCollege: [''],
        majorGraduate: [''],
        yearGraduatedGraduate: [''],
        expectedGradeGraduate: ['']
      }),
      testScores: this.formBuilder.group({
        satOverall: [''],
        satEnglish: [''],
        satMath: [''],
        actOverall: [''],
        actEnglish: [''],
        actMath: [''],
        gre: [''],
        gmat: [''],
        ielts: [''],
        toefl: [''],
        ap: ['']
      }),
      extracurricularActivities: [''],
      shortBio: [''],
      employmentHistory: this.formBuilder.array([])
    })
  }

  createFreshEmployementHistory(): FormGroup {
    return this.formBuilder.group({
      companyName: ['', Validators.required],
      role: ['', Validators.required]
    });
  }

  get employeeHistoryList(): FormArray {
    return this.newPeerForm.get('employmentHistory') as FormArray;
  }

  addCompany(): void {
    this.submitted = false;
    this.employeeHistoryList.push(this.createFreshEmployementHistory());
    this.currentActivityIndex++;
  }
  removeCompany(index: number): void {
    this.employeeHistoryList.removeAt(index);
    this.currentActivityIndex--;
  }

  ngOnInit(): void {
    this.peerToEditId = this.route.snapshot.paramMap.get('id') || '';
    if (this.peerToEditId) {
      this.getPeerToEditDetails();
    }
    this.initializeForm();
    this.loadCountries();
    this.genders = [
      { name: 'Male', value: 1 },
      { name: 'Female', value: 2 },
      { name: 'Other', value: 3 }
    ]
  }

  getPeerToEditDetails() {
    this.peerReviewService.getPeerReviewerById(this.peerToEditId).subscribe({
      next: (response) => {
        this.peerToEdit = response;
        this.populatePeerForm();
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: "Error in fetching peer details" });
      }
    })
  }

  onSubmit(): void {
    if (this.peerToEditId) {
      this.onSubmitEditPeer();
    } else {
      this.onSubmitAddPeer();
    }
  }

  private onSubmitEditPeer() {
    this.isLoading = true;
    this.submitted = true;
    this.newPeerForm.markAllAsTouched();
    if (this.newPeerForm.invalid) {
      this.isLoading = false;
      return;
    }
    const formData = this.newPeerForm.value;
    const adminData: UpdatePeerReviewerRequest = {
      applicationUserId: this.peerToEditId,
      firstName: formData.peerInformation.firstName,
      lastName: formData.peerInformation.lastName,
      email: formData.peerInformation.emailAddress,
      gender: formData.peerInformation.gender.value, // Type casting
      phoneNumber: formData.peerInformation.contactNumber,
      imageUrl: this.attachment,
      city: formData.peerProfile.city,
      country: formData.peerProfile.country,
      homeCity: formData.peerProfile.originCity,
      homeCountry: formData.peerProfile.originCountry,
      highSchoolName: formData.highSchoolInformation.nameOfHighSchool,
      highSchoolBoard: formData.highSchoolInformation.board,
      tenthGrades: formData.highSchoolInformation.classXGrades,
      twelvethGrades: formData.highSchoolInformation.classXIIGrades,
      underGraduateCollageName: formData.undergraduateCollegeInformation.nameOfUndergraduateCollege,
      underGraduateCollageCountry: formData.undergraduateCollegeInformation.countryOfUndergraduateCollege,
      underGraduateCollageCity: formData.undergraduateCollegeInformation.cityOfUndergraduateCollege,
      underGraduateMajorOrDegree: formData.undergraduateCollegeInformation.majorUndergraduate,
      underGraduateOrExpectedUnderGraduationCompletionYear: formData.undergraduateCollegeInformation.yearGraduatedUndergraduate,
      underGraduateExpectedGrades: formData.undergraduateCollegeInformation.expectedGradeUndergraduate,
      graduateCollageName: formData.graduateCollegeInformation.nameOfGraduateCollege,
      graduateCollageCountry: formData.graduateCollegeInformation.countryOfGraduateCollege,
      graduateCollageCity: formData.graduateCollegeInformation.cityOfGraduateCollege,
      graduateMajorOrDegree: formData.graduateCollegeInformation.majorGraduate,
      graduateOrExpectedGraduationCompletionYear: formData.graduateCollegeInformation.yearGraduatedGraduate,
      graduateExpectedGrades: formData.graduateCollegeInformation.expectedGradeGraduate,
      satOverallScore: formData.testScores.satOverall,
      satMathScore: formData.testScores.satMath,
      satEnglishScore: formData.testScores.satEnglish,
      actOverallScore: formData.testScores.actOverall,
      extraCurricularActivitiesDescription: formData.extracurricularActivities,
      shortBio: formData.shortBio,
      employmentHistory: this.mapEmploymentHistory(formData.employmentHistory), // Type casting
      isEmailUpdate: formData.peerInformation.emailAddress !== this.originalEmailAddress
    };
    this.peerReviewService.updatePeerReviewerDetails(adminData).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.submitted = false;
        this.getPeerToEditDetails();
        this.fileUploader.clear();
        this.attachment = undefined;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Peer updated successfully' });
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error while updating Peer' });
      }
    })
  }

  private onSubmitAddPeer() {
    this.isLoading = true;
    this.submitted = true;
    this.newPeerForm.markAllAsTouched();
    if (this.newPeerForm.invalid) {
      this.isLoading = false;
      return;
    }
    const formData = this.newPeerForm.value;
    const adminData: RegisterPeerReviewerRequest = {
      firstName: formData.peerInformation.firstName,
      lastName: formData.peerInformation.lastName,
      gender: formData.peerInformation.gender.value, // Type casting
      email: formData.peerInformation.emailAddress,
      phoneNumber: formData.peerInformation.contactNumber,
      imageUrl: this.attachment,
      city: formData.peerProfile.city,
      country: formData.peerProfile.country,
      homeCity: formData.peerProfile.originCity,
      homeCountry: formData.peerProfile.originCountry,
      highSchoolName: formData.highSchoolInformation.nameOfHighSchool,
      highSchoolBoard: formData.highSchoolInformation.board,
      tenthGrades: formData.highSchoolInformation.classXGrades,
      twelvethGrades: formData.highSchoolInformation.classXIIGrades,
      underGraduateCollageName: formData.undergraduateCollegeInformation.nameOfUndergraduateCollege,
      underGraduateCollageCountry: formData.undergraduateCollegeInformation.countryOfUndergraduateCollege,
      underGraduateCollageCity: formData.undergraduateCollegeInformation.cityOfUndergraduateCollege,
      underGraduateMajorOrDegree: formData.undergraduateCollegeInformation.majorUndergraduate,
      underGraduateOrExpectedUnderGraduationCompletionYear: formData.undergraduateCollegeInformation.yearGraduatedUndergraduate,
      underGraduateExpectedGrades: formData.undergraduateCollegeInformation.expectedGradeUndergraduate,
      graduateCollageName: formData.graduateCollegeInformation.nameOfGraduateCollege,
      graduateCollageCountry: formData.graduateCollegeInformation.countryOfGraduateCollege,
      graduateCollageCity: formData.graduateCollegeInformation.cityOfGraduateCollege,
      graduateMajorOrDegree: formData.graduateCollegeInformation.majorGraduate,
      graduateOrExpectedGraduationCompletionYear: formData.graduateCollegeInformation.yearGraduatedGraduate,
      graduateExpectedGrades: formData.graduateCollegeInformation.expectedGradeGraduate,
      satOverallScore: formData.testScores.satOverall,
      satMathScore: formData.testScores.satMath,
      satEnglishScore: formData.testScores.satEnglish,
      actOverallScore: formData.testScores.actOverall,
      extraCurricularActivitiesDescription: formData.extracurricularActivities,
      shortBio: formData.shortBio,
      employmentHistory: this.mapEmploymentHistory(formData.employmentHistory), // Type casting
      state: PeerReviewerState.Active
    };
    this.peerReviewService.registerPeerReviewer(adminData).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.submitted = false;
        this.newPeerForm.reset();
        this.fileUploader.clear();
        this.attachment = undefined;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Peer added successfully' });
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error adding Peer' });
      }
    })
  }

  private mapEmploymentHistory(raw: any[]): { companyName: string; role: string }[] {
    return raw.map(item => ({
      companyName: item.companyName,
      role: item.role
    }));
  }

  onFileSelect(event: any): void {
    const file = event.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.uploadedImageUrl = e.target.result;
      };
      reader.readAsDataURL(file);
      this.attachment = file;
    }
  }
  onFileRemoved() {
    this.attachment = undefined;
    if (this.fileUploader) {
      this.fileUploader.clear();
      this.uploadedImageUrl = undefined;   // ✅ This clears the UI preview
    }
    this.cdr.detectChanges();      // Force UI refresh
  }

  peerList() {
    this.router.navigate(['/admin', 'peer-list']);
  }
}
